import { useNavigate, useSearch } from "@tanstack/react-router";
import type { SafetyVideoCategories } from "@unbox/api";
import { CursorBox, Eye, FilterLines, SearchMd, Shield02, ShieldTick } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useAssuranceVideos, useAssuranceVideosDownload } from "@/api/assurance/assurance-videos";
import { Tab, TabList, Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { VideoFilterPanel } from "@/components/filters/panels";
import { PageLayout } from "@/components/page-layout";
import { generateFilterSyncConfig, useAutoFilterUrlSync } from "@/hooks/use-auto-filter-sync";
import { useDebounce } from "@/hooks/use-debounce";
import { useUrlFilterSync } from "@/hooks/use-url-filter-sync";
import { WITH_CREATOR_ACTIONS } from "@/lib/assurance/action-types";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import type { FilterField } from "@/providers/filter-registry";
import type { flaggedVideosRoute } from "@/router";
import { downloadCSV } from "@/utils/download-csv";
import { FLAGGED_CSV_HEADERS, FLAGGED_CSV_PRETTY_HEADERS, toFlaggedVideoCsvRow } from "./csv";
import { buildFlaggedVideoParams } from "./params";
import { TabReview } from "./tabs/TabReview/TabReview";
import { TabRiskResolved } from "./tabs/TabRiskResolved/TabRiskResolved";
import { TabWithCreator } from "./tabs/TabWithCreator/TabWithCreator";

type TabId = "needs-review" | "with-creator" | "risk-resolved";

// Fetches just the totals per category so the tab badges track the tables. Views
// filtering is server-side, so passing min/max views keeps the badge count in sync
// with the table even while a views filter is active.
const useCategoryCount = (category: SafetyVideoCategories, search: string, actionTaken?: string[]) => {
  const { apiFilters } = useFilters();
  const { data } = useAssuranceVideos({
    ...buildFlaggedVideoParams(apiFilters, search),
    not:
      actionTaken === apiFilters.actionTaken ? apiFilters.not : apiFilters.not?.filter((key) => key !== "actionTaken"),
    category,
    actionTaken,
    pagination: { limit: 1, offset: 0 },
  });
  return data?.total;
};

const SafetyFlaggedContent = () => {
  // Route-agnostic so the same page works under both the merchant-scoped
  // (/merchant/...) and workspace-scoped (/workspace/.../merchant/...) routes,
  // which share the same search schema. flaggedVideosRoute.useSearch()/useNavigate()
  // are strict to that one route ID, so they'd break under the workspace-scoped route.
  const currentSearch = useSearch({ strict: false }) as ReturnType<(typeof flaggedVideosRoute)["useSearch"]>;
  const navigate = useNavigate() as ReturnType<(typeof flaggedVideosRoute)["useNavigate"]>;
  const { tab, video_id } = currentSearch;
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { apiFilters, reset, form, activeFields } = useFilters();
  const debouncedSearch = useDebounce(search, 400);

  useAutoFilterUrlSync(form, currentSearch, activeFields);
  const { fieldMapping, currentSearch: filterSearch } = useMemo(
    () => generateFilterSyncConfig(activeFields, currentSearch),
    [activeFields, currentSearch],
  );
  useUrlFilterSync({ form, fieldMapping, navigate, currentSearch: filterSearch });

  const needsReviewCount = useCategoryCount("pendingManualReview", debouncedSearch);
  const withCreatorCount = useCategoryCount("actionInProgress", debouncedSearch, [...WITH_CREATOR_ACTIONS]);
  const riskResolvedCount = useCategoryCount("loggedAsComplete", debouncedSearch, apiFilters.actionTaken);

  const formatBadge = (count: number | undefined) => (count === undefined ? undefined : String(count));

  const tabs = [
    {
      id: "needs-review",
      label: "Needs Review",
      icon: <Eye className="w-4 h-4" />,
      badge: formatBadge(needsReviewCount),
    },
    {
      id: "with-creator",
      label: "With Creator to Action",
      icon: <CursorBox className="w-4 h-4" />,
      badge: formatBadge(withCreatorCount),
    },
    {
      id: "risk-resolved",
      label: "Risk Resolved",
      icon: <ShieldTick className="w-4 h-4" />,
      badge: formatBadge(riskResolvedCount),
    },
  ] satisfies { id: TabId; label: string; icon: React.ReactNode; badge?: string }[];

  // Each tab is its own queue (category + optional actionTaken); the export mirrors the
  // active tab's params and its badge total so it downloads exactly what the table shows.
  const exportConfig: Record<TabId, { category: SafetyVideoCategories; actionTaken?: string[]; total?: number }> = {
    "needs-review": { category: "pendingManualReview", total: needsReviewCount },
    "with-creator": {
      category: "actionInProgress",
      actionTaken: [...WITH_CREATOR_ACTIONS],
      total: withCreatorCount,
    },
    "risk-resolved": { category: "loggedAsComplete", actionTaken: apiFilters.actionTaken, total: riskResolvedCount },
  };
  const { category: exportCategory, actionTaken: exportActionTaken, total: exportTotal } = exportConfig[tab as TabId];

  const { mutateAsync: downloadVideos, isPending: isDownloading } = useAssuranceVideosDownload();

  const handleExport = async () => {
    if (!exportTotal) return;
    const videos = await downloadVideos({
      ...buildFlaggedVideoParams(apiFilters, debouncedSearch),
      category: exportCategory,
      actionTaken: exportActionTaken,
      total: exportTotal,
    });
    downloadCSV(
      videos.map(toFlaggedVideoCsvRow),
      "flagged-videos",
      [...FLAGGED_CSV_HEADERS],
      FLAGGED_CSV_PRETTY_HEADERS,
    );
  };

  const onVideoIdChange = (id: string | undefined) =>
    navigate({
      search: (prev) => ({ ...prev, video_id: id }),
    });

  const tabContent: Record<TabId, React.ReactNode> = {
    "needs-review": <TabReview selectedId={video_id} onSelectId={onVideoIdChange} search={debouncedSearch} />,
    "with-creator": <TabWithCreator selectedId={video_id} onSelectId={onVideoIdChange} search={debouncedSearch} />,
    "risk-resolved": <TabRiskResolved selectedId={video_id} onSelectId={onVideoIdChange} search={debouncedSearch} />,
  };

  return (
    <PageLayout
      title="Flagged videos"
      subtitle="Review flagged risks, track resolution progress, and see which risky videos have been resolved"
      icon={Shield02}
      backgroundColor="#FFFCF9"
      fitViewport
      callToAction={
        <Tooltip title="No flagged videos to export" isDisabled={!!exportTotal}>
          <ExportCsvButton
            onClick={handleExport}
            isDownloading={isDownloading}
            disabled={!exportTotal}
            label="Export as CSV"
          />
        </Tooltip>
      }
    >
      {/* Tabs + Search + Filters */}
      <div className="flex flex-row items-center justify-between flex-shrink-0">
        <Tabs
          selectedKey={tab}
          // video_id isn't shared across tabs — each has its own list, so clear it on
          // switch instead of trying to find a video that may not be in the new tab's
          // list, which fell back to a broken selection instead of the first row.
          onSelectionChange={(key) =>
            navigate({ search: (prev) => ({ ...prev, tab: key as TabId, video_id: undefined }) })
          }
        >
          <TabList type="underline" items={tabs}>
            {(t) => (
              <Tab
                key={t.id}
                id={t.id}
                label={t.label}
                icon={t.icon}
                badge={t.badge}
                badgeInactiveClassName="bg-primary"
              />
            )}
          </TabList>
        </Tabs>

        <div className="flex items-center flex-row gap-2">
          <Input
            size="sm"
            inputClassName="text-sm"
            className="w-64"
            value={search}
            onChange={setSearch}
            placeholder="Search..."
            icon={SearchMd}
          />
          <Button
            color="secondary"
            size="sm"
            iconLeading={<FilterLines className="w-4 h-4" />}
            onClick={() => setFiltersOpen((prev) => !prev)}
          >
            {filtersOpen ? "Hide Filters" : "Filters"}
          </Button>
        </div>
      </div>

      {/* Tab content */}
      {tabContent[tab as TabId]}

      <VideoFilterPanel open={filtersOpen} close={() => setFiltersOpen(false)} clearAll={reset} timePeriod="all_time" />
    </PageLayout>
  );
};

const BASE_ACTIVE_FIELDS: FilterField[] = [
  "minViews",
  "maxViews",
  "publishDateStart",
  "publishDateEnd",
  "flaggedDateStart",
  "flaggedDateEnd",
  "riskId",
  "riskTier",
  "creatorType",
  "hooks",
  "tactics",
  "sellingPoints",
  "videoStyles",
  "videoLanguages",
  "campaign",
];

export const SafetyFlagged = () => {
  // See the comment in SafetyFlaggedContent below on why this stays route-agnostic.
  const search = useSearch({ strict: false }) as ReturnType<(typeof flaggedVideosRoute)["useSearch"]>;

  const activeFields = useMemo(
    () => (search.tab === "risk-resolved" ? [...BASE_ACTIVE_FIELDS, "actionTaken" as FilterField] : BASE_ACTIVE_FIELDS),
    [search.tab],
  );

  return (
    <FiltersProvider activeFields={activeFields} initialValues={search}>
      <SafetyFlaggedContent />
    </FiltersProvider>
  );
};
